import { useCallback, useState } from 'react';

/**
 * Choix d'affichage gardé pour la personne (ADR-149 §1) : une **préférence locale**, dans le navigateur,
 * par vue et par identité. Jamais une autorisation : un choix perdu (navigation privée, stockage
 * bloqué, valeur que la vue n'offre plus) ramène au mode par défaut de la vue.
 */

const PREFIX = 'ayumia.display.';

export function readDisplayPreference(key: string, allowed: readonly string[]): string | undefined {
  try {
    const value = globalThis.localStorage?.getItem(PREFIX + key) ?? undefined;
    return value !== undefined && allowed.includes(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function writeDisplayPreference(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(PREFIX + key, value);
  } catch {
    // Stockage indisponible : le choix vaut pour la page ouverte seulement.
  }
}

/** Clé de préférence : la vue et la personne (aucune donnée sensible ; l'identifiant reste dans le navigateur). */
export function displayPreferenceKey(viewKey: string, context: Readonly<Record<string, unknown>>): string {
  const identity = typeof context['identity_id'] === 'string' ? context['identity_id'] : 'session';
  return `${identity}.${viewKey}`;
}

/**
 * Mode affiché : le choix gardé s'il est encore offert, sinon `fallback`. `key` nul : rien n'est gardé
 * (`display.remember: false`).
 */
export function useDisplayPreference(key: string | null, allowed: readonly string[], fallback: string): [string, (mode: string) => void] {
  const [mode, setMode] = useState<string>(() => (key === null ? undefined : readDisplayPreference(key, allowed)) ?? fallback);
  const shown = allowed.includes(mode) ? mode : fallback;
  const choose = useCallback(
    (next: string) => {
      setMode(next);
      if (key !== null) writeDisplayPreference(key, next);
    },
    [key],
  );
  return [shown, choose];
}
